import { getAnalytics } from "@tm/insurance/analytics";
import {
  trackComponentView,
  trackEvent,
  triggerPageExit,
} from "@tm/insurance/analytics/xdm";
import { navigationActions } from "@tm/insurance/navigation";
import { usePlanType } from "@tm/ui";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import { ROUTE_CONSTANTS } from "libs/utility/src/lib/insurance/navigation/routeConstants";
import React, { useCallback, useState } from "react";
import { IPlan } from "../../pages/InsuranceCommonLanding/types";
import EducationModal from "../EducationModal/EducationModal";
import FormattedText, { TextNodeType } from "../FormattedText/FormattedText";
import {
  PlanCard,
  PlanDetails,
  PlanIcon,
  PlanIconContainer,
  ProtectionPlans,
  ProtectionTitle,
  TagContainer,
  TagImage,
  TitleContainer,
  TravelProtectionWrapper,
} from "./TravelProtection.style";

interface TravelProtectionProps {
  title: TextNodeType;
  plans: IPlan[];
}

const TravelProtection: React.FC<TravelProtectionProps> = ({
  title,
  plans,
}) => {
  const { setType } = usePlanType();
  const [activePlan, setActivePlan] = useState<IPlan | null>(
    plans?.[0] || null
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const analytics = getAnalytics();

  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    trackComponentView({
      name: "travel_protection_plans_shown",
      type: "card",
    });
  }, []);

  const { ref: intersectionRef } = useIntersectionObserver(handleIntersection, {
    threshold: 0.5, // Trigger when 50% of component is visible
    triggerOnLeaveViewport: true,
  });

  // Education modal tracking handlers
  const handleEducationModalShown = () => {
    analytics?.trackLandingEvents?.({ actionType: "educationModal_shown" });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_shown",
      },
    });
  };
  const handleEducationModalNextClick = () => {
    analytics?.trackLandingEvents?.({
      actionType: "educationModal_next_click",
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_next_click",
      },
    });
  };
  const handleEducationModalSkipClick = () => {
    analytics?.trackLandingEvents?.({
      actionType: "educationModal_skip_click",
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_skip_click",
      },
    });
  };
  const handleEducationModalPageShown = (pageId: string) => {
    analytics?.trackLandingEvents?.({
      actionType: `educationModal_${pageId}_shown`,
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: `educationModal_${pageId}_shown`,
      },
    });
  };

  const handleEducationSkip = () => {
    setShowModal(false);
    setType(activePlan?.type);
    navigationActions.navigateToLanding({ type: activePlan?.type }, false);
  };

  const handlePlanClick = (plan: IPlan) => {
    analytics?.trackCommonLandingEvents({
      actionType: `protection_plan_${plan.type}_clicked`,
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: `protection_plan_${plan.type}_clicked`,
      },
    });
    triggerPageExit();
    if (plan.showPages) {
      setActivePlan(plan);
      setCurrentPageIndex(0);
      setShowModal(true);
    } else {
      setType(plan.type);
      navigationActions.navigateToRouteNameWithHostname(
        ROUTE_CONSTANTS.INSURANCE_V1_LANDING,
        { type: plan?.type }
      );
    }
  };

  return (
    <TravelProtectionWrapper ref={intersectionRef}>
      <ProtectionTitle>
        {title && <FormattedText value={title} />}
      </ProtectionTitle>

      <ProtectionPlans>
        {plans
          ?.filter((plan) => plan.type === "flt-delay")
          .map((plan, index) => (
            <PlanCard key={index} onClick={() => handlePlanClick(plan)}>
              <PlanIconContainer
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <PlanIcon src={plan.icon} alt={"planIcon"} />
              </PlanIconContainer>
              <PlanDetails>
                <TitleContainer>
                  {plan.title && (
                    <FormattedText
                      value={plan.title}
                      baseStyle={{ fontWeight: 700, fontSize: 18 }}
                    />
                  )}
                  {plan.tagURL && (
                    <TagContainer>
                      <TagImage src={plan.tagURL} alt="New" />
                    </TagContainer>
                  )}
                </TitleContainer>
                {plan?.info && (
                  <FormattedText
                    value={plan?.info}
                    baseStyle={{ fontSize: 14, color: "#4a4a4a" }}
                  />
                )}
              </PlanDetails>
            </PlanCard>
          ))}
      </ProtectionPlans>

      {showModal && activePlan && activePlan.showPages && (
        <EducationModal
          pagesData={activePlan.pages}
          knowMore={activePlan.knowMore}
          setShowEducationModal={setShowModal}
          skipButtonHandler={handleEducationSkip}
          onModalShown={handleEducationModalShown}
          onNextClick={handleEducationModalNextClick}
          onSkipClick={handleEducationModalSkipClick}
          onPageShown={handleEducationModalPageShown}
        />
      )}
    </TravelProtectionWrapper>
  );
};

export default TravelProtection;
